import React, { useMemo, useState } from 'react';
import { Table, Space, InputNumber, Button, Typography, Empty, Modal, Checkbox, Tooltip } from 'antd';
import { FileExcelOutlined, PrinterOutlined } from '@ant-design/icons';
import XLSX from 'xlsx-js-style';
import dayjs from 'dayjs';
import { formatTS } from '../utils/time';

// Eval Progress > Client Report: the client's audit tracker for each chosen
// campaign and the month. Per campaign, one row per agent (Full Time, then
// Part Time), their completed evaluations in call date order as Call 1..N,
// each with Monitor Date (call date), Master Contact ID, Score and Feedback
// Date (date the evaluation was created). Call columns grow to the agent with
// the most evals. Hovering a Score shows the evaluation's Feedback (the form
// question tagged as Feedback), marked by a dotted underline; on screen only,
// not in the export. Scores at or below the Red / Yellow values are coloured, on
// screen and in the Excel export (a sheet per campaign), which the QA manager
// then edits as needed. Print All Evaluations opens every one of these
// evaluations, ready to print, with the evaluation page's print options.
//
// agents:      the Eval Progress rows (UserID, DisplayName, AgentFullTime, campaigns)
// report:      { campaigns: [{ ID, Name }], evals: [{ ID, AgentUserID, CampaignID, CallDate, ContactID, Score, CreatedAt }] }
// campaignIds: the campaigns searched (one = every agent is listed)

const COMPANY = 'Superior Contact';
const RED_FILL    = 'FFC7CE';
const YELLOW_FILL = 'FFEB9C';

// Dates as YYYY-MM-DD: the call date as stored, the feedback date (a UTC
// timestamp) in the app's time zone.
const callDay     = (v) => (v ? String(v).slice(0, 10) : null);
const feedbackDay = (v) => (v ? formatTS(v, 'YYYY-MM-DD') : null);
const showDate    = (d) => (d ? dayjs(d).format('M/D/YYYY') : '');
// YYYY-MM-DD -> Excel date serial (days since 1899-12-30).
const excelSerial = (d) => {
  const [y, m, day] = d.split('-').map(Number);
  return (Date.UTC(y, m - 1, day) - Date.UTC(1899, 11, 30)) / 86400000;
};

// One campaign's rows: Full Time agents, then Part Time, then anyone with no
// Paycor record, each group by name. With one campaign searched every agent
// is listed (no evals = blank cells); with several, an agent is listed under
// a campaign they have evaluations or calls on.
function buildRows(agents, evals, campaignId, listEveryone) {
  const byAgent = {};
  evals.filter((e) => e.CampaignID === campaignId)
    .forEach((e) => { (byAgent[e.AgentUserID] ||= []).push(e); });
  const people = (agents || [])
    .filter((a) => listEveryone || byAgent[a.UserID]
      || (a.campaigns || []).some((c) => c.CampaignID === campaignId))
    .map((a) => ({
      key: `a${a.UserID}`,
      Name: a.DisplayName || '',
      PaycorStatus: a.PaycorStatus,
      PaycorHireDate: a.PaycorHireDate,
      FullTime: a.AgentFullTime,
      calls: byAgent[a.UserID] || [],
    }));
  const byName = (x, y) => x.Name.localeCompare(y.Name);
  const ft = people.filter((p) => p.FullTime === true || p.FullTime === 1).sort(byName);
  const pt = people.filter((p) => p.FullTime === false || p.FullTime === 0).sort(byName);
  const unknown = people.filter((p) => p.FullTime == null).sort(byName);
  const rows = [...ft];
  if (pt.length) rows.push({ key: 'h-pt', section: 'Part Time Agents' }, ...pt);
  if (unknown.length) rows.push({ key: 'h-unk', section: 'Not in Paycor' }, ...unknown);
  return { rows, callCount: Math.max(0, ...people.map((p) => p.calls.length)) };
}

export default function EvalClientReport({ agents, report, month, campaignIds }) {
  const [red,    setRed]    = useState(50);
  const [yellow, setYellow] = useState(90);
  // Print All Evaluations: the evaluation page's print options.
  const [printOpen,      setPrintOpen]      = useState(false);
  const [printComments,  setPrintComments]  = useState(false);
  const [printHideAgent, setPrintHideAgent] = useState(false);
  const [printCoaching,  setPrintCoaching]  = useState(false);

  const scoreFill = (score) => {
    if (score == null) return null;
    if (red != null && score <= red) return RED_FILL;
    if (yellow != null && score <= yellow) return YELLOW_FILL;
    return null;
  };

  const year = month ? month.format('YYYY') : '';
  const listEveryone = (campaignIds || []).length === 1;

  const reports = useMemo(() => (report?.campaigns || []).map((c) => ({
    campaign: c,
    title: `${COMPANY}, ${c.Name} ${year}`.trim(),
    ...buildRows(agents, report.evals || [], c.ID, listEveryone),
  })), [agents, report, year, listEveryone]);

  // Every evaluation in the report, in report order: campaign, agent, call date.
  const printIds = useMemo(() => reports.flatMap((r) =>
    r.rows.filter((row) => !row.section).flatMap((row) => row.calls.map((c) => c.ID))), [reports]);

  if (!report) return null;

  const columnsFor = (callCount) => {
    const totalCols = 1 + callCount * 4;
    const sectionCell = (r) => (r.section ? { colSpan: 0 } : {});
    return [
      {
        title: 'Name', key: 'Name', fixed: 'left', width: 200,
        onCell: (r) => (r.section ? { colSpan: totalCols } : {}),
        // Name with the Paycor status and hire date under it, as on the Eval
        // Counts tab. On screen only; the export keeps the name.
        render: (_, r) => {
          if (r.section) return <strong>{r.section}</strong>;
          const lower = (r.PaycorStatus || '').toLowerCase();
          const statusColor = lower === 'active' ? '#52c41a' : lower === 'terminated' ? '#ff4d4f' : '#8c8c8c';
          return (
            <div>
              <div>{r.Name}</div>
              {(r.PaycorStatus || r.PaycorHireDate) && (
                <div style={{ fontSize: 12, color: '#595959', lineHeight: 1.5, marginTop: 2 }}>
                  {r.PaycorStatus && <span style={{ color: statusColor }}>{r.PaycorStatus}</span>}
                  {r.PaycorStatus && r.PaycorHireDate && <span> · </span>}
                  {r.PaycorHireDate && <span>{r.PaycorHireDate}</span>}
                </div>
              )}
            </div>
          );
        },
      },
      ...Array.from({ length: callCount }, (_, i) => ({
        title: `Call ${i + 1}`,
        key: `call${i}`,
        children: [
          { title: 'Monitor Date', key: `md${i}`, width: 100, onCell: sectionCell,
            // Opens the evaluation in a new tab, so the report stays put. On
            // screen only: the export keeps it a plain date.
            render: (_, r) => {
              const c = r.calls?.[i];
              if (!c) return '';
              return (
                <a href={`/cases/${c.ID}`} target="_blank" rel="noopener noreferrer">
                  {showDate(callDay(c.CallDate))}
                </a>
              );
            } },
          { title: 'Master Contact ID / Case', key: `cid${i}`, width: 140, onCell: sectionCell,
            render: (_, r) => r.calls?.[i]?.ContactID ?? '' },
          { title: 'Score', key: `sc${i}`, width: 70, align: 'center',
            onCell: (r) => {
              if (r.section) return { colSpan: 0 };
              const fill = scoreFill(r.calls?.[i]?.Score);
              // Dark text on the red / yellow fill in both themes: in dark
              // mode the score was white on the pale colour.
              return fill ? { style: { background: `#${fill}`, color: '#1f2937' } } : {};
            },
            render: (_, r) => {
              const c = r.calls?.[i];
              if (!c) return '';
              return c.Feedback ? (
                <Tooltip title={<div style={{ whiteSpace: 'pre-wrap' }}>{c.Feedback}</div>} overlayStyle={{ maxWidth: 500 }}>
                  <span style={{ borderBottom: '1px dotted currentColor', cursor: 'help' }}>{c.Score ?? '—'}</span>
                </Tooltip>
              ) : (c.Score ?? '');
            } },
          { title: 'Feedback Date', key: `fd${i}`, width: 100, onCell: sectionCell,
            render: (_, r) => showDate(feedbackDay(r.calls?.[i]?.CreatedAt)) },
        ],
      })),
    ];
  };

  const exportExcel = () => {
    const border = { style: 'thin', color: { rgb: 'BFBFBF' } };
    const box = { top: border, bottom: border, left: border, right: border };
    const head = { font: { bold: true }, fill: { fgColor: { rgb: 'D9E1F2' } }, border: box,
                   alignment: { horizontal: 'center', vertical: 'center', wrapText: true } };
    const cell = (v, extra = {}) => ({ v: v ?? '', t: typeof v === 'number' ? 'n' : 's', s: { border: box, ...extra } });
    // Real Excel dates: a date serial shown as m/d/yyyy.
    const dateCell = (d) => (d
      ? { v: excelSerial(d), t: 'n', z: 'm/d/yyyy', s: { border: box, numFmt: 'm/d/yyyy', alignment: { horizontal: 'center' } } }
      : cell(''));

    const wb = XLSX.utils.book_new();
    const usedNames = new Set();
    reports.forEach(({ campaign, title, rows, callCount }) => {
      const totalCols = 1 + callCount * 4;
      const aoa = [];
      aoa.push([{ v: title, t: 's', s: { font: { bold: true, sz: 14 }, alignment: { horizontal: 'center' } } }]);
      const callRow = [{ v: '', t: 's', s: head }];
      const subRow  = [{ v: 'NAME', t: 's', s: head }];
      for (let i = 0; i < callCount; i += 1) {
        callRow.push({ v: `Call ${i + 1}`, t: 's', s: head }, { v: '', t: 's', s: head }, { v: '', t: 's', s: head }, { v: '', t: 's', s: head });
        subRow.push(...['Monitor Date', 'MASTER CONTACT ID/ CASE', 'Score', 'Feedback Date'].map((h) => ({ v: h, t: 's', s: head })));
      }
      aoa.push(callRow, subRow);
      rows.forEach((r) => {
        if (r.section) {
          aoa.push([{ v: r.section, t: 's', s: { font: { bold: true }, fill: { fgColor: { rgb: 'D9E1F2' } } } }]);
          return;
        }
        const row = [cell(r.Name)];
        for (let i = 0; i < callCount; i += 1) {
          const c = r.calls[i];
          const fill = scoreFill(c?.Score);
          row.push(
            dateCell(callDay(c?.CallDate)),
            cell(c?.ContactID != null ? String(c.ContactID) : ''),
            cell(c?.Score ?? '', { alignment: { horizontal: 'center' }, ...(fill ? { fill: { fgColor: { rgb: fill } } } : {}) }),
            dateCell(feedbackDay(c?.CreatedAt)),
          );
        }
        aoa.push(row);
      });
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws['!merges'] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: Math.max(totalCols - 1, 0) } },
        ...Array.from({ length: callCount }, (_, i) => ({ s: { r: 1, c: 1 + i * 4 }, e: { r: 1, c: 4 + i * 4 } })),
      ];
      ws['!cols'] = [{ wch: 30 }, ...Array.from({ length: callCount }, () => [{ wch: 12 }, { wch: 18 }, { wch: 7 }, { wch: 12 }]).flat()];
      // Sheet names: the campaign, at most 31 characters, no \ / ? * [ ] :, unique.
      let name = (campaign.Name || 'Campaign').replace(/[\\/?*[\]:]/g, '-').slice(0, 31);
      for (let n = 2; usedNames.has(name.toLowerCase()); n += 1) name = `${name.slice(0, 27)} (${n})`;
      usedNames.add(name.toLowerCase());
      XLSX.utils.book_append_sheet(wb, ws, name);
    });
    const monthName = month ? month.format('MMMM') : '';
    const label = reports.length === 1 ? reports[0].campaign.Name : 'Client';
    XLSX.writeFile(wb, `${label} ${year} Audit Tracker - ${monthName}.xlsx`.replace(/[\\/:*?"<>|]/g, '-'));
  };

  // Opens the print page in a new tab: every evaluation in the report, one per
  // page, laid out as the evaluation page prints it.
  const printAll = () => {
    setPrintOpen(false);
    const params = new URLSearchParams({ ids: printIds.join(',') });
    if (printComments) params.set('comments', '1');
    if (printComments && printHideAgent) params.set('hide_agent', '1');
    if (printCoaching) params.set('coaching', '1');
    window.open(`/eval-print?${params.toString()}`, '_blank', 'noopener');
  };

  const anyRows = reports.some((r) => r.rows.length);

  return (
    <div>
      <Space wrap style={{ marginBottom: 12 }} align="center">
        <span>Red Scores At/Below:</span>
        <InputNumber min={0} max={100} value={red} onChange={setRed} style={{ width: 80 }} />
        <span>Yellow Scores At/Below:</span>
        <InputNumber min={0} max={100} value={yellow} onChange={setYellow} style={{ width: 80 }} />
        <Button icon={<FileExcelOutlined />} onClick={exportExcel} disabled={!anyRows}>Export to Excel</Button>
        <Button icon={<PrinterOutlined />} onClick={() => setPrintOpen(true)} disabled={!printIds.length}>
          Print All Evaluations ({printIds.length})
        </Button>
      </Space>

      {!anyRows && <Empty description="No agents found for the selected filters." />}
      {reports.map(({ campaign, title, rows, callCount }) => (rows.length ? (
        <div key={campaign.ID} style={{ marginBottom: 24 }}>
          <Typography.Title level={5} style={{ marginTop: 0 }}>{title}</Typography.Title>
          <Table
            rowKey="key"
            columns={columnsFor(callCount)}
            dataSource={rows}
            size="small"
            bordered
            pagination={false}
            scroll={{ x: 'max-content' }}
          />
        </div>
      ) : null))}

      {/* Same choices as Print on an evaluation. */}
      <Modal maskClosable={false}
        title="Print Options"
        open={printOpen}
        onCancel={() => setPrintOpen(false)}
        onOk={printAll}
        okText="Print"
        okButtonProps={{ icon: <PrinterOutlined /> }}
        width={340}
      >
        <Space direction="vertical" style={{ padding: '8px 0' }} size={12}>
          <Checkbox checked={printComments} onChange={(e) => setPrintComments(e.target.checked)}>
            Include comments
          </Checkbox>
          <Checkbox style={{ marginLeft: 24 }} disabled={!printComments}
            checked={printHideAgent} onChange={(e) => setPrintHideAgent(e.target.checked)}>
            Hide agent comments
          </Checkbox>
          <Checkbox checked={printCoaching} onChange={(e) => setPrintCoaching(e.target.checked)}>
            Include coaching sessions list
          </Checkbox>
        </Space>
      </Modal>
    </div>
  );
}
